"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.zDisableTotpRequest = exports.zConfirmTotpRequest = exports.zVerifyTotpRequest = exports.zChangePasswordFirstLoginRequest = exports.zChangePasswordRequest = exports.PASSWORD_REQUIREMENTS = exports.PASSWORD_SYMBOLS = exports.PASSWORD_MIN_LENGTH = exports.zUpdateProfileRequest = exports.zSelectTenantRequest = exports.zLoginRequest = exports.zTenantRole = exports.TENANT_ROLES = void 0;
exports.isPasswordComplexEnough = isPasswordComplexEnough;
const zod_1 = require("zod");
/**
 * The three MVP roles. Kept as a plain union (backed by a Postgres CHECK
 * constraint, not a pg ENUM — see db/schema/tenancy.ts) rather than an
 * open string, so both the DB and the API reject a typo'd role the same
 * way. `technician` is deliberately last: most technicians never log in
 * at all (see db/schema/technicians.ts) and this list order has no
 * significance beyond readability.
 */
exports.TENANT_ROLES = ["owner", "staff", "technician"];
exports.zTenantRole = zod_1.z.enum(exports.TENANT_ROLES);
exports.zLoginRequest = zod_1.z.object({
    email: zod_1.z.string().trim().toLowerCase().email(),
    password: zod_1.z.string().min(1),
});
exports.zSelectTenantRequest = zod_1.z.object({
    tenantId: zod_1.z.number().int().positive(),
});
exports.zUpdateProfileRequest = zod_1.z.object({
    fullName: zod_1.z.string().trim().min(1),
    // The login identifier itself (users.email) — enforced unique and
    // lowercase at the DB level (see db/schema/tenancy.ts's own check
    // constraint), so a collision surfaces as a 409 from AuthService, not a
    // client-side check.
    email: zod_1.z.string().trim().toLowerCase().email().optional(),
});
/**
 * The single source of truth for this app's password complexity policy —
 * server (zNewPassword below), the temporary-password generator
 * (admin/temporary-password.ts), and both password-change forms
 * (Owner Profile, first-login) all read from this instead of each
 * hardcoding its own copy. Owner's explicit call, superseding the earlier
 * "floor, not a full policy" decision (there's still no forgot-password
 * flow, but a fixed, always-displayed requirement list removes the
 * lockout risk that justified skipping this originally).
 */
exports.PASSWORD_MIN_LENGTH = 8;
/** Display order for the UI — matches exactly what's shown as the
 * requirement. */
exports.PASSWORD_SYMBOLS = "!@#$%^&*()+-_";
// Same characters as PASSWORD_SYMBOLS, reordered so `-` is last -- inside
// a `[...]` character class `-` is only literal (not a range operator)
// when it isn't between two other characters.
const PASSWORD_SYMBOL_CLASS = "!@#$%^&*()+_-";
exports.PASSWORD_REQUIREMENTS = [
    { id: "length", label: `At least ${exports.PASSWORD_MIN_LENGTH} characters`, test: (p) => p.length >= exports.PASSWORD_MIN_LENGTH },
    { id: "lowercase", label: "A lowercase letter (a-z)", test: (p) => /[a-z]/.test(p) },
    { id: "uppercase", label: "An uppercase letter (A-Z)", test: (p) => /[A-Z]/.test(p) },
    { id: "number", label: "A number (0-9)", test: (p) => /[0-9]/.test(p) },
    {
        id: "symbol",
        label: `A symbol (${exports.PASSWORD_SYMBOLS})`,
        test: (p) => new RegExp(`[${PASSWORD_SYMBOL_CLASS}]`).test(p),
    },
];
function isPasswordComplexEnough(password) {
    return exports.PASSWORD_REQUIREMENTS.every((r) => r.test(password));
}
const zNewPassword = zod_1.z
    .string()
    .min(exports.PASSWORD_MIN_LENGTH, `Must be at least ${exports.PASSWORD_MIN_LENGTH} characters.`)
    .refine(isPasswordComplexEnough, {
    message: "Must include a lowercase letter, an uppercase letter, a number, and a symbol.",
});
exports.zChangePasswordRequest = zod_1.z.object({
    currentPassword: zod_1.z.string().min(1),
    newPassword: zNewPassword,
});
/** POST /auth/change-password-first-login — no currentPassword field: the
 * pending-password-change cookie issued after `needs_password_change` IS
 * the proof of identity (same role a totp-pending cookie plays for 2FA),
 * so asking for the temporary password again would be redundant, not
 * extra security. */
exports.zChangePasswordFirstLoginRequest = zod_1.z.object({
    newPassword: zNewPassword,
});
exports.zVerifyTotpRequest = zod_1.z.object({
    /** Either a 6-digit TOTP code or a backup code — AuthService tries the
     * TOTP secret first, then falls back to unused backup codes, so the
     * frontend never needs to know which kind the user is typing. */
    code: zod_1.z.string().trim().min(1),
    rememberDevice: zod_1.z.boolean().default(false),
});
exports.zConfirmTotpRequest = zod_1.z.object({
    code: zod_1.z.string().trim().min(1),
});
exports.zDisableTotpRequest = zod_1.z.object({
    currentPassword: zod_1.z.string().min(1),
    code: zod_1.z.string().trim().min(1),
});
//# sourceMappingURL=auth.js.map